import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Star } from "lucide-react";
import { Logo } from "@/components/Logo";
import { BOOK_URL } from "@/lib/booking";

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "Services",
    children: [
      { label: "All Services", to: "/services" },
      { label: "Pricing", to: "/pricing" },
      { label: "Service Areas", to: "/areas" },
    ],
  },
  { label: "Work", to: "/work" },
  { label: "Reviews", to: "/reviews" },
  {
    label: "About",
    children: [
      { label: "The Property Curator", to: "/about" },
      { label: "My Story", to: "/about/my-story" },
    ],
  },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

function DesktopLink({ item }) {
  const [open, setOpen] = useState(false);
  if (item.children) {
    return (
      <div
        className="relative"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          className="font-mono-label text-[#0B1215] hover:text-[#FF5A36] flex items-center gap-1 transition-colors"
          data-testid={`nav-link-${item.label.toLowerCase()}`}
        >
          {item.label}
          <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute top-full left-0 pt-3" data-testid={`nav-dropdown-${item.label.toLowerCase()}`}>
            <div className="bg-[#F8F9FA] border-2 border-[#0B1215] min-w-[220px] py-2 shadow-[4px_4px_0px_0px_rgba(11,18,21,1)]">
              {item.children.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  className={({ isActive }) =>
                    `block px-5 py-2.5 font-mono-label transition-colors ${
                      isActive ? "bg-[#0B1215] text-[#F8F9FA]" : "text-[#0B1215] hover:bg-[#FF5A36] hover:text-[#F8F9FA]"
                    }`
                  }
                  data-testid={`nav-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        `font-mono-label transition-colors ${isActive ? "text-[#FF5A36]" : "text-[#0B1215] hover:text-[#FF5A36]"}`
      }
      data-testid={`nav-link-${item.label.toLowerCase()}`}
    >
      {item.label}
    </NavLink>
  );
}

function MobileGroup({ item, onClose }) {
  const [open, setOpen] = useState(false);
  if (item.children) {
    return (
      <div className="border-b border-[#0B1215]/10">
        <button
          onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center justify-between py-3 font-mono-label"
          data-testid={`nav-mobile-group-${item.label.toLowerCase()}`}
        >
          {item.label}
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="pb-3 pl-4 flex flex-col gap-1">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={onClose}
                className="font-mono-label py-2 text-[#495057] hover:text-[#FF5A36]"
                data-testid={`nav-mobile-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                → {c.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onClose}
      className="font-mono-label py-3 border-b border-[#0B1215]/10 block"
      data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
    >
      {item.label}
    </NavLink>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled ? "bg-[#F8F9FA]/90 backdrop-blur-xl border-b border-[#0B1215]/10" : "bg-[#F8F9FA]/60 backdrop-blur-sm"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link">
          <Logo size="md" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <DesktopLink key={item.label} item={item} />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-1.5 border border-[#0B1215]/20 hover:border-[#FF5A36] hover:text-[#FF5A36] px-2.5 py-1.5 font-mono-label text-[11px] tracking-tight whitespace-nowrap transition-colors"
            data-testid="nav-google-rating"
            aria-label="4.8 stars on Google — read all reviews"
          >
            <Star size={12} strokeWidth={2.5} className="fill-[#FF5A36] text-[#FF5A36]" />
            <span>4.8 on Google · read reviews</span>
          </Link>
          <Link
            to="/#launch-offer"
            className="inline-flex items-center gap-2 bg-[#FF5A36] hover:bg-[#E04928] text-[#F8F9FA] px-3 py-1.5 font-mono-label transition-colors"
            data-testid="nav-launch-offer"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full bg-[#F8F9FA] opacity-75" />
              <span className="relative inline-flex h-2 w-2 bg-[#F8F9FA]" />
            </span>
            £50 OFF
          </Link>
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-[#0B1215] text-[#F8F9FA] px-6 py-3 font-mono-label hover:bg-[#FF5A36] transition-colors active:scale-95"
            data-testid="nav-quote-btn"
          >
            Book now →
          </a>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F8F9FA] border-t-2 border-[#0B1215]">
          <div className="px-6 py-4">
            {NAV.map((item) => (
              <MobileGroup key={item.label} item={item} onClose={() => setOpen(false)} />
            ))}
            <Link
              to="/reviews"
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex items-center gap-1.5 border border-[#0B1215]/20 px-3 py-2 font-mono-label text-xs"
              data-testid="nav-mobile-google-rating"
            >
              <Star size={12} strokeWidth={2.5} className="fill-[#FF5A36] text-[#FF5A36]" />
              <span>4.8 on Google · read reviews</span>
            </Link>
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="block bg-[#0B1215] text-[#F8F9FA] px-6 py-3 font-mono-label text-center mt-4"
              data-testid="nav-mobile-quote-btn"
            >
              Book now →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
